import { FC } from 'react'

import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'
import { SettingsPageLayout } from 'SettingsV2/components/SettingsPageLayout'
import { AllocationStrategySettings } from './AllocationStrategySettings.tsx'
import { RecognitionStartSettings } from './RecognitionStartSettings'
import { XeroLedgerAccountMappings } from './XeroLedgerAccountMappings'
import type {
  RevenueRecognitionSettingsControls,
  RevenueRecognitionSettingsUpdate
} from './revenueRecognitionSettings.types'

export const RevenueRecognitionSettingsPage: FC = () => {
  const { displayNotification } = useNotifications()
  const revenueRecognitionSettings = useQuery(
    dashboard20240730Client.getRevenueRecognitionSettings
  )
  const revenueRecognitionSettingsMutator = useMutation(
    dashboard20240730Client.putRevenueRecognitionSettings,
    {
      onSuccess() {
        displayNotification('Revenue recognition settings updated', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Revenue recognition settings failed to update', {
          type: 'error'
        })
      }
    }
  )

  const updateSettings: RevenueRecognitionSettingsControls['updateSettings'] = (
    settings: RevenueRecognitionSettingsUpdate,
    onError
  ) => {
    revenueRecognitionSettingsMutator.mutate(
      { body: settings },
      { onError: () => onError?.() }
    )
  }

  const settingsControls: RevenueRecognitionSettingsControls = {
    settings: revenueRecognitionSettings.data ?? undefined,
    isLoading: revenueRecognitionSettings.isLoading,
    isUpdating: revenueRecognitionSettingsMutator.isPending,
    updateSettings
  }

  return (
    <SettingsPageLayout header="Revenue recognition">
      <RecognitionStartSettings {...settingsControls} />
      <AllocationStrategySettings {...settingsControls} />
      <XeroLedgerAccountMappings />
    </SettingsPageLayout>
  )
}
